import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ShieldCheck, Search, Loader2, Download, Pencil, Plus, Trash2, Landmark, ClipboardList, Inbox } from 'lucide-react';
import DealComplianceReviewModal from './DealComplianceReviewModal';
import FintracRegister from './FintracRegister';
import FinalPackageQueue from './FinalPackageQueue';
import { openAuthed } from '../lib/authSession';

const OUTCOME_STYLES = {
  approved: 'bg-emerald-100 text-emerald-800',
  approved_with_conditions: 'bg-amber-100 text-amber-800',
  deficient: 'bg-red-100 text-red-800',
  pending: 'bg-gray-100 text-gray-700',
};
const OUTCOME_LABELS = {
  approved: 'Approved',
  approved_with_conditions: 'Approved w/ Conditions',
  deficient: 'Deficient',
  pending: 'Pending Review',
};

export const OutcomeBadge = ({ outcome }) => (
  <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${OUTCOME_STYLES[outcome] || OUTCOME_STYLES.pending}`} data-testid={`deal-outcome-${outcome || 'pending'}`}>
    {OUTCOME_LABELS[outcome] || 'Pending Review'}
  </span>
);

/**
 * AdminDealComplianceTab — standalone, admin-filled Deal Compliance Reviews
 * (not linked to agent client folders). List + create / edit / PDF / delete.
 */
const AdminDealComplianceTab = ({ apiUrl, adminPassword }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null); // { reviewId: string|null, queueItem?: object }
  const [view, setView] = useState('queue'); // queue | reviews | fintrac
  const [refreshKey, setRefreshKey] = useState(0);
  const [queueCounts, setQueueCounts] = useState({ for_review: 0, reviewed: 0, total: 0 });
  const headers = useMemo(() => ({ 'X-Admin-Password': adminPassword }), [adminPassword]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/deal-compliance/reviews`, { headers });
      if (r.ok) setReviews(await r.json());
    } catch (e) {
      console.error('Failed to load reviews:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, headers]);

  useEffect(() => { load(); }, [load]);

  const deleteReview = async (r) => {
    if (!window.confirm(`Delete the compliance review for "${r.client_names}" (rev ${r.revision})? This cannot be undone.`)) return;
    const res = await fetch(`${apiUrl}/api/admin/deal-compliance/reviews/${r.id}`, { method: 'DELETE', headers });
    if (res.ok) { load(); setRefreshKey(k => k + 1); } else alert('Delete failed.');
  };

  const visible = reviews.filter(r => {
    const q = search.toLowerCase();
    const matches = !q || [r.client_names, r.broker, r.lender, r.property_address, r.signed_name].some(v => (v || '').toLowerCase().includes(q));
    if (!matches) return false;
    if (filter === 'all') return true;
    if (filter === 'fintrac') return !!r.fintrac_report_filed;
    return (r.outcome || 'pending') === filter;
  });

  const counts = useMemo(() => ({
    total: reviews.length,
    pending: reviews.filter(r => (r.outcome || 'pending') === 'pending').length,
    deficient: reviews.filter(r => r.outcome === 'deficient').length,
    approved: reviews.filter(r => r.outcome === 'approved').length,
    fintrac: reviews.filter(r => r.fintrac_report_filed).length,
  }), [reviews]);

  return (
    <Card data-testid="admin-deal-compliance-tab">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#0077B6]" />
              Deal Compliance Review — Mortgage
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Final Compliance Packages created in each agent's My Clients' Folder land here marked <b>For Review</b>. Open the package,
              fill in the review form manually, tick the documents verified, record the outcome and FINTRAC notes, and sign. Reviews are admin-only and editable.
            </p>
          </div>
          <Button className="bg-[#0077B6]" onClick={() => setModal({ reviewId: null })} data-testid="deal-review-new-btn">
            <Plus className="h-4 w-4 mr-1" />New Review
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className={`px-2 py-1 rounded font-semibold ${queueCounts.for_review ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-500'}`} data-testid="deal-count-for-review">{queueCounts.for_review} package(s) for review</span>
            <span className="px-2 py-1 rounded bg-gray-100 text-gray-700" data-testid="deal-count-total">{counts.total} reviews</span>
            <span className="px-2 py-1 rounded bg-slate-100 text-slate-700" data-testid="deal-count-pending">{counts.pending} pending</span>
            <span className="px-2 py-1 rounded bg-red-100 text-red-800" data-testid="deal-count-deficient">{counts.deficient} deficient</span>
            <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800" data-testid="deal-count-approved">{counts.approved} approved</span>
            <span className="px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="deal-count-fintrac">{counts.fintrac} FINTRAC reports filed</span>
          </div>
          <div className="flex items-center gap-1 bg-gray-100 rounded-md p-1" data-testid="deal-view-toggle">
            <button type="button" onClick={() => setView('queue')} className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 ${view === 'queue' ? 'bg-white shadow text-orange-700' : 'text-gray-600'}`} data-testid="deal-view-queue">
              <Inbox className="h-3.5 w-3.5" /> For Review
              {queueCounts.for_review > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="deal-view-queue-badge">{queueCounts.for_review}</span>}
            </button>
            <button type="button" onClick={() => setView('reviews')} className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 ${view === 'reviews' ? 'bg-white shadow text-[#0077B6]' : 'text-gray-600'}`} data-testid="deal-view-reviews">
              <ClipboardList className="h-3.5 w-3.5" /> Reviews
            </button>
            <button type="button" onClick={() => setView('fintrac')} className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 ${view === 'fintrac' ? 'bg-white shadow text-amber-700' : 'text-gray-600'}`} data-testid="deal-view-fintrac">
              <Landmark className="h-3.5 w-3.5" /> FINTRAC Register
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {view === 'queue' ? (
          <FinalPackageQueue apiUrl={apiUrl} adminPassword={adminPassword} refreshKey={refreshKey} onCounts={setQueueCounts}
            onStartReview={(item) => setModal({ reviewId: null, queueItem: item })}
            onEditReview={(item) => setModal({ reviewId: item.review_id, queueItem: item })} />
        ) : view === 'fintrac' ? (
          <FintracRegister apiUrl={apiUrl} adminPassword={adminPassword} refreshKey={refreshKey} onEdit={(id) => setModal({ reviewId: id })} />
        ) : (
        <>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Search client, broker, lender, property…" value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0077B6]" data-testid="deal-search" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md" data-testid="deal-filter">
            <option value="all">All reviews</option>
            <option value="pending">Pending review</option>
            <option value="approved">Approved</option>
            <option value="approved_with_conditions">Approved with conditions</option>
            <option value="deficient">Deficient</option>
            <option value="fintrac">FINTRAC report filed</option>
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500" data-testid="deal-review-empty">
            {reviews.length === 0 ? 'No deal compliance reviews yet. Click "New Review" to create the first one.' : 'No reviews match.'}
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2">Client / Deal</th>
                  <th className="text-left px-3 py-2">Broker</th>
                  <th className="text-left px-3 py-2">Lender</th>
                  <th className="text-left px-3 py-2">Closing</th>
                  <th className="text-left px-3 py-2">Outcome</th>
                  <th className="text-left px-3 py-2">FINTRAC</th>
                  <th className="text-left px-3 py-2">Last Reviewed</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visible.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50" data-testid={`deal-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-800">{r.client_names}</p>
                      {r.transaction_type && <p className="text-xs text-gray-600" data-testid={`deal-row-txn-${r.id}`}>{r.transaction_type}</p>}
                      {r.property_address && <p className="text-xs text-gray-500 truncate max-w-[220px]">{r.property_address}</p>}
                      {r.folder_id && <p className="text-[10px] text-[#0077B6] font-semibold" data-testid={`deal-row-linked-${r.id}`}>From {r.agent_name ? `${r.agent_name}'s` : 'agent'} client folder</p>}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{r.broker || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{r.lender || '—'}</td>
                    <td className="px-3 py-2 text-gray-700 text-xs">{r.closing_date || '—'}</td>
                    <td className="px-3 py-2"><OutcomeBadge outcome={r.outcome} /></td>
                    <td className="px-3 py-2 text-xs">{r.fintrac_report_filed ? <span className="text-amber-800 font-semibold">Filed</span> : <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {new Date(r.updated_at).toLocaleString()}<br /><span className="text-gray-400">{r.signed_name} • rev {r.revision}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" asChild>
                          <a href={`${apiUrl}/api/admin/deal-compliance/reviews/${r.id}/pdf`} onClick={openAuthed} target="_blank" rel="noopener noreferrer" data-testid={`deal-pdf-${r.id}`}>
                            <Download className="h-4 w-4 mr-1" />PDF
                          </a>
                        </Button>
                        <Button size="sm" className="bg-[#0077B6]" onClick={() => setModal({ reviewId: r.id })} data-testid={`deal-review-btn-${r.id}`}>
                          <Pencil className="h-4 w-4 mr-1" />Edit
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteReview(r)} data-testid={`deal-review-delete-${r.id}`}>
                          <Trash2 className="h-4 w-4 mr-1" />Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}
      </CardContent>

      {modal && (
        <DealComplianceReviewModal
          apiUrl={apiUrl}
          adminPassword={adminPassword}
          reviewId={modal.reviewId}
          queueItem={modal.queueItem}
          onClose={() => setModal(null)}
          onSaved={() => { load(); setRefreshKey(k => k + 1); }}
        />
      )}
    </Card>
  );
};

export default AdminDealComplianceTab;
